import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaCheckCircle } from 'react-icons/fa';
import { Card, CardContent } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

function MainContent() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [feedbackText, setFeedbackText] = useState(''); // optional feedback text
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const navigate = useNavigate();

  // Function to handle rating submission + optional feedback
  const handleSubmitRating = async () => {
    // Prevent double submits
    if (submitting) return;
    setSubmitting(true);
    setErrorMsg(null);

    try {
      const payload = {
        rating: rating || null, // allow 0/null meaning no rating
        feedback_text: feedbackText?.trim() ? feedbackText.trim() : null // handled below for JS
      };

      // Build body: only include feedback_text if there is text
      const body = {};
      if (payload.rating !== null) body.rating = payload.rating;
      if (feedbackText && feedbackText.trim().length > 0) body.feedback_text = feedbackText.trim();

      const res = await fetch('/api/submit-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit rating');
      }

      // Show the feedback popup
      setShowPopup(true);

      // Auto-close popup and navigate after 3 seconds
      setTimeout(() => {
        setShowPopup(false);
        navigate('/user-home');
      }, 3000);
    } catch (error) {
      console.error('Error submitting rating:', error);
      setErrorMsg(error.message || 'Error submitting rating');
      // keep popup hidden — let user try again
    } finally {
      setSubmitting(false);
    }
  };

  // Function to navigate directly to /user-home when the button is clicked
  const handleGoHome = () => {
    navigate('/user-home');
  };

  return (
    <>
      <section className="relative w-full h-[400px] bg-cover bg-center flex items-center justify-center text-center" style={{ backgroundImage: "url('https://cdn.builder.io/api/v1/image/assets/TEMP/8f6ffffc8ab802e782577c926cb810536465d5963e873d3949a61f12e02fdbef?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214')" }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
          <Heading level="h1" className="text-5xl font-bold text-white mb-4">Thank You for the Interview!</Heading>
          <Text className="text-xl font-semibold text-white/90 max-w-2xl">
            We value your feedback! Please rate your experience to help us
            improve and better serve your needs in the future.
          </Text>
        </div>
      </section>

      <section className="flex justify-center py-5 gap-10">
        <Card className="bg-[#cbcbcb] rounded-2xl p-5 w-[375px] text-center">
          <CardContent className="p-0">
            <Heading level="h2" className="text-foreground text-3xl font-medium tracking-wide mb-2.5">
              Thank You for Taking the <br />
              Interview!
            </Heading>

            <div className="mb-6">
              <Text className="mb-4">Please rate your Interview Experience</Text>
              <div className="flex gap-2 justify-center" aria-label="Star rating">
                {[...Array(5)].map((_, index) => {
                  const starValue = index + 1;
                  return (
                    <FaStar
                      key={index}
                      size={30}
                      color={starValue <= (hover || rating) ? "#ffc107" : "#e4e5e9"}
                      onClick={() => setRating(starValue)}
                      onMouseEnter={() => setHover(starValue)}
                      onMouseLeave={() => setHover(null)}
                      className="cursor-pointer transition-colors duration-200"
                      aria-label={`${starValue} star`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setRating(starValue);
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <Label htmlFor="feedbackText" className="block mb-2 font-semibold">
                Optional: Share any feedback (what went well / suggestions)
              </Label>
              <Textarea
                id="feedbackText"
                name="feedbackText"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Type your feedback here (optional)"
                rows={5}
                className="w-full"
              />
            </div>

            {errorMsg && <div className="text-destructive mb-4" role="alert">{errorMsg}</div>}

            <div className="flex gap-3 justify-center items-center mb-4">
              <Button
                onClick={handleSubmitRating}
                disabled={submitting}
                aria-disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Rating'}
              </Button>

              <Button
                variant="secondary"
                onClick={handleGoHome}
                className="bg-black text-white border-none"
              >
                Go Back to Home
              </Button>
            </div>

            <Text className="mt-4">
              We look forward to hearing from you!
            </Text>
          </CardContent>
        </Card>
      </section>

      <div className="flex justify-end items-center mt-5">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/e995bceca220d93026442c253dc606525838ad6bd655d0969b86a97adf0c66ad?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214"
          alt="Decorative"
          className="max-w-md"
        />
      </div>

      {/* Popup Message */}
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex flex-col items-center gap-2">
              <FaCheckCircle color="#4BB543" size={50} />
              <span>Thank You for Your Feedback!</span>
            </DialogTitle>
            <DialogDescription className="text-center">
              We appreciate you taking the time to rate your interview experience.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MainContent;
